import React from 'react';
import { Button, Col, Row, Spinner } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { PDFDownloadLink } from '@react-pdf/renderer';
import RedemptionHistoryPDF from './RedemptionHistoryPDF';
import { useAuth } from '../../hooks/useAuth';
import { WebApiCalls } from '../../webApiCalls/WebApiCalls';
import { toast } from 'react-toastify';
import RedeemBranchModal from '../Modals/RedeemBranchModal';

const RedemptionHistoryDetail = () => {
    const { state } = useLocation();
    const { redemption_detail: data } = state || {};
    const { getToken, user } = useAuth();
    const [redemption, setRedemption] = React.useState(data);
    const [showRedeemModal, setShowRedeemModal] = React.useState(false);
    const [redeemLoading, setRedeemLoading] = React.useState(false);

    const handleRedeem = async (selectedBranchId) => {
        const token = getToken();
        if (!token || !redemption?._id) return;
        if (!selectedBranchId) {
            toast.error('Please select the branch where the redemption occurred.');
            return;
        }
        setRedeemLoading(true);
        try {
            const res = await new WebApiCalls().vendorRedeemRedemption(token, {
                redemptionId: redemption._id,
                redemptionValue: redemption.redemptionValue,
                redeemedBranch: selectedBranchId
            });
            if (res?.code === 200) {
                toast.success('Redemption marked as redeemed.');
                // Rehydrate branches with original details if missing
                let updated = res.data;
                if (Array.isArray(updated?.branches) && Array.isArray(redemption?.branches)) {
                    updated.branches = updated.branches.map((b) => {
                        if (typeof b === 'string' || (typeof b === 'object' && !b.name)) {
                            // Find full branch info from original redemption
                            return redemption.branches.find((orig) => orig._id === (b._id || b)) || b;
                        }
                        return b;
                    });
                }
                setRedemption(updated);
                setShowRedeemModal(false);
            } else {
                toast.error(res?.desc || 'Failed to redeem.');
            }
        } catch (err) {
            toast.error('Error redeeming redemption.');
        } finally {
            setRedeemLoading(false);
        }
    };

    if (!redemption) {
        return (
            <div className="text-center">
                <Spinner animation="border" variant="success" />
            </div>
        );
    }

    return (
        <>
            <Row className="justify-content-end align-items-center">
                <Col sm={4} md={4} className="d-flex justify-content-end align-items-center gap-2">
                    <PDFDownloadLink document={<RedemptionHistoryPDF data={redemption} />} fileName="redemption-details.pdf">
                        {({ loading }) =>
                            loading ? (
                                <Spinner animation="border" variant="success" />
                            ) : (
                                <Button className="export-file justify-content-center w-140 align-items-center" style={{ background: '#198754', padding: '9px' }}>
                                    Export PDF
                                </Button>
                            )
                        }
                    </PDFDownloadLink>
                    {/* Show Redeem button for vendors if not already redeemed */}
                    {user?.role === 'vendor' && !redemption?.redeemed && (
                        <>
                            <Button
                                className="export-file justify-content-center w-140 align-items-center"
                                style={{ background: '#198754', padding: '9px' }}
                                onClick={() => setShowRedeemModal(true)}
                            >
                                Redeem
                            </Button>
                            <RedeemBranchModal
                                show={showRedeemModal}
                                handleClose={() => setShowRedeemModal(false)}
                                branches={redemption?.branches}
                                onRedeem={handleRedeem}
                                loading={redeemLoading}
                            />
                        </>
                    )}
                    <Link to="/redemptions">
                        <Button className="theme-cancel-btn px-5 py-2">Close</Button>
                    </Link>
                </Col>
            </Row>
            <Row className="d-flex justify-content-center">
                <Col xs={12} sm={12} md={11} lg={11} className="p-4 m-3">
                    <Row>
                        <Col md={6}>
                            <div className="detail-small-heading">Reward Title</div>
                            <div className="detail-value">{redemption?.offer?.name || 'N/A'}</div>
                        </Col>
                        {user?.role !== 'commuter' && (
                            <Col md={6}>
                                <div className="detail-small-heading">TR Rewards Member</div>
                                <div className="detail-value">{redemption?.commuter?.name || 'N/A'}</div>
                            </Col>
                        )}
                    </Row>
                    <Row className="mt-4">
                        <Col md={6}>
                            <div className="detail-small-heading">Redemption Value</div>
                            <div className="detail-value">{redemption?.redemptionValue || 'N/A'}</div>
                        </Col>
                        <Col md={6}>
                            <div className="detail-small-heading">Redemption Date</div>
                            <div className="detail-value">
                                {redemption?.redeemedAt
                                    ? new Date(redemption?.redeemedAt).toLocaleString()
                                    : redemption?.addedToWalletAt
                                    ? new Date(redemption?.addedToWalletAt).toLocaleString()
                                    : 'N/A'}
                            </div>
                        </Col>
                    </Row>
                    <Row className="mt-4">
                        <Col md={6}>
                            <div className="detail-small-heading">Branches</div>
                            {Array.isArray(redemption?.branches) && redemption.branches.length > 0 ? (
                                <table className="table table-bordered detail-value">
                                    <thead>
                                        <tr>
                                            <th className="text-center">#</th>
                                            <th>Name</th>
                                            <th>Location</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {redemption.branches.map((branch, idx) => (
                                            <tr key={branch._id || idx}>
                                                <td className="text-center">{idx + 1}</td>
                                                <td>{branch.name}</td>
                                                <td>{branch.address}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="detail-value">N/A</div>
                            )}
                        </Col>
                        <Col md={6}>
                            <div className="detail-small-heading">QR Code</div>
                            <div className="detail-value">
                                {redemption?.qrCode ? (
                                    <img src={redemption.qrCode} alt="QR Code" width={100} height={100} style={{ border: '1px solid #ccc', borderRadius: '4px' }} />
                                ) : (
                                    <span>N/A</span>
                                )}
                            </div>
                        </Col>
                    </Row>
                    <Row className="mt-4">
                        <Col md={6}>
                            <div className="detail-small-heading">Redeemed Branch</div>
                            <div className="detail-value">
                                {redemption?.redeemedBranch?.name ? (
                                    <>
                                        <div>{redemption.redeemedBranch.name}</div>
                                        <div>{redemption.redeemedBranch.address}</div>
                                    </>
                                ) : (
                                    <span>N/A</span>
                                )}
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="detail-small-heading">Redemption Status</div>
                            <div className="detail-value">{redemption?.redeemed ? 'Redeemed' : 'Not Redeemed'}</div>
                        </Col>
                    </Row>
                    {redemption?.redeemed && (
                        <Row className="mt-4">
                            <Col md={12}>
                                <div className="detail-small-heading text-success">Status</div>
                                <div className="detail-value text-success">
                                    Redeemed at {redemption?.redeemedAt ? new Date(redemption.redeemedAt).toLocaleString() : ''}
                                </div>
                            </Col>
                        </Row>
                    )}
                </Col>
            </Row>
        </>
    );
};

export default RedemptionHistoryDetail;
